import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, useDataTableContext } from 'components/DataTable'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Spinner } from 'react-bootstrap'
import apiService from 'services/api'
import * as S from '../CommonTable.styles'
import { TableSearch } from '../../Filters/TableSearch'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import styled from 'styled-components'
import { ColumnsModal } from '../../Modals/ColumnsModal/ColumnsModal'
import { ContactResponse } from 'models/contacts'
import { usePaginationURLParams } from 'utils/usePaginationURLParams'
import { createColumnHelper } from '@tanstack/react-table'
import { k12FilterStore, useFilterParams } from 'components/Filters/FilterStore'
import FilterChips from 'components/Filters/components/FilterChips'
import { K12ChainProxy } from 'models/k12'
import { Anchor } from 'components/UI/Anchor'
import { K12Filterset } from './K12Filterset'
import { formatInteger } from 'utils/formatting'
import { FbLink } from '../../FbUI/FbLink'
import CreateDealModal from '../../Deals/CreateDealModal/CreateDealModal'
import TableButtonAction from '../../Buttons/TableButtons/TableButtonAction'
import { BsBriefcase, BsDownload, BsTags, BsTruck } from 'react-icons/bs'
import TagAssignModal from '../../Modals/AssignmentModal/TagAssignModal'
import DistributorAssignModal from '../../Modals/AssignmentModal/DistributorAssignModal'
import {
  AccountOwnerCell,
  DistributorsCell,
  SaleStagesCellFromDeals,
  TaglistCell,
} from '../../FbUI/StagePill'
import { K12DistrictExportModal } from '../../Modals/ExportModal/K12DistrictExportModal'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { IColumnSort } from 'components/DataTable/types'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'

export type ContactsTableRef = {
  refetch: () => void
}
type SortableFields = (keyof ContactResponse | string)[]

const sortableFields: SortableFields = [
  'deal_count',
  'contact_count',
  'note_count',
  'sales_stages',
  'k12district__lea_name',
  'k12district__lcity',
  'k12district__lstate',
  'k12district__zipcode',
  'k12district__locale',
  'k12district__gslo',
  'k12district__gshi',
  'k12district__student_count',
  'k12district__teachers_count',
  'k12district__operational_schools',
  'k12district__revenue',
  'k12district__food_service_expenditures',
  'k12district__free_lunch',
  'k12district__reduced_lunch',
  'k12district__total_supplemented_lunch',
  'k12district__uncategorized_lunch',
  'k12district__free_lunch_perc',
  'k12district__reduced_lunch_perc',
  'k12district__total_supplemented_lunch_perc',
  'k12district__uncategorized_lunch_perc',
  'k12district__estimated_meals_per_day',
  'k12district__county',
]

const PAGE_SIZE = 100

const K12TableComponent = forwardRef(
  (
    {
      setTotalRowsCount,
      pageParamName = 'page',
      categoryId,
      subCategoryId,
    }: {
      setTotalRowsCount?: (count: number) => void
      pageParamName?: string
      categoryId?: number
      subCategoryId?: number
    },
    ref
  ) => {
    const api = apiService()
    const tableContext = useDataTableContext()
    const queryClient = useQueryClient()
    const {
      state: { sorting, tableKey, rowSelection, totalSelectedRows },
      methods: { clearSelectedRows, setTotalRowsInBackend },
    } = useDataTableContext()

    const [pagination, setPagination] = usePaginationURLParams(
      PAGE_SIZE,
      pageParamName
    )

    const filterParams = useFilterParams(k12FilterStore)

    const sortParams = useMemo(() => {
      const params: Record<string, unknown> = {}

      if (sorting?.length) {
        params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
      }

      return params
    }, [sorting])

    const filterAndSortParams = useMemo(
      () => ({
        ...filterParams,
        ...sortParams,
      }),
      [filterParams, sortParams]
    )

    const TABLE_QUERY_KEY = getTableQueryKey({
      tableKey: tableKey,
      filterParams: filterAndSortParams,
      page: pagination.pageIndex + 1,
    })

    // DATA FETCHING
    const { isLoading, data, isFetching, refetch } = useQuery({
      queryKey: TABLE_QUERY_KEY,
      queryFn: async () => {
        await queryClient.cancelQueries(TABLE_QUERY_KEY)
        clearSelectedRows()

        const res = await api.getK12DistrictsList({
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          ...filterAndSortParams,
        })
        setTotalRowsCount?.(res.count)
        setTotalRowsInBackend(res.count)
        return res
      },
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
    })
    // END DATA FETCHING

    useEffect(() => {
      clearSelectedRows()
    }, [data])

    const selectedIds = useMemo(() => {
      const ids: number[] = []
      const selection = Object.keys(rowSelection)
      for (const s of selection) {
        const id = data?.results[parseInt(s)]?.id
        if (id) {
          ids.push(id)
        }
      }
      return ids
    }, [rowSelection])

    const [showAssignTags, setShowAssignTags] = useState(false)
    const [showAssignDistributors, setShowAssignDistributors] = useState(false)
    const [openDealModal, setOpenDealModal] = useState(false)
    const [dealCompanuIds, setDealCompanyIds] = useState<number[]>([])
    const [showExportAllModal, setShowExportAllModal] = useState(false)

    const handleCreateDeals = () => {
      if (!data) return
      const companyIds = Object.keys(rowSelection).map(
        (idx) => data.results[parseInt(idx)].id
      )
      setDealCompanyIds(companyIds)
      setOpenDealModal(true)
    }
    // Make refetch available to parent
    useImperativeHandle(
      ref,
      () =>
        ({
          refetch: () => refetch(),
        }) as ContactsTableRef
    )

    const columns = useK12Columns({
      sorting,
    })

    useEffect(() => {
      setTotalRowsCount?.(data?.count ?? 0)
    }, [data?.count])

    return (
      <TableContainer>
        <S.SearchContainer>
          <S.SearchContainerFilters>
            <TableSearch
              filterStore={k12FilterStore}
              searchPlaceholder="Search schools by name"
              className="flex-1"
            />
            <K12Filterset />
            <ColumnsModal />
            <TableButtonAction
              items={[
                {
                  label: 'Create Deals',
                  icon: <BsBriefcase size={20} />,
                  callback: handleCreateDeals,
                },
                {
                  label: 'Manage Tags',
                  icon: <BsTags size={20} />,
                  callback: () => setShowAssignTags(true),
                },
                {
                  label: 'Add Known Distributors',
                  icon: <BsTruck size={20} />,
                  callback: () => setShowAssignDistributors(true),
                },
                {
                  label: 'Export Selected',
                  icon: <BsDownload size={20} />,
                  callback: () => setShowExportAllModal(true),
                },
              ]}
              disabled={!totalSelectedRows}
              selectedRowsCount={totalSelectedRows ?? 0}
            />
          </S.SearchContainerFilters>
          <FilterChips clearAllButton showActive store={k12FilterStore} />
        </S.SearchContainer>

        <TagAssignModal
          show={showAssignTags}
          handleClose={() => setShowAssignTags(false)}
          tableQueryKey={TABLE_QUERY_KEY}
          data={data?.results ?? []}
          filterAndSortParams={filterAndSortParams}
          apiTagsAction={api.k12BulkEditTags}
          idAccessor="id"
        />

        <DistributorAssignModal
          show={showAssignDistributors}
          handleClose={() => setShowAssignDistributors(false)}
          tableQueryKey={TABLE_QUERY_KEY}
          data={data?.results ?? []}
          idAccessor="id"
          filterAndSortParams={filterAndSortParams}
          apiDistributorsAction={api.k12BulkEditDistributors}
        />

        <K12DistrictExportModal
          show={showExportAllModal}
          _handleClose={() => {
            setShowExportAllModal(false)
          }}
          filters={{
            ...filterAndSortParams,
            id: selectedIds,
          }}
          count={selectedIds.length || data?.count || 0}
        />

        <DataTable
          key={tableContext.state.tableKey}
          autoLayout
          data={data?.results ?? []}
          columns={columns}
          sortableFields={sortableFields}
          virtualizeRows={true}
          enableRowSelection
          isPaginationEnabled={true}
          defaultSort={[
            {
              id: 'k12district__estimated_meals_per_day',
              desc: true,
            } as IColumnSort<object>,
          ]}
          paginationOptions={{
            pageCount: Math.ceil((data?.count ?? 0) / PAGE_SIZE),
            setPagination: setPagination,
            pagination: pagination,
            isPaginationLoading: isLoading,
          }}
        >
          <DataTable.FooterControls>
            <OpportunitiesTableFooterControls
              categoryId={categoryId}
              subCategoryId={subCategoryId}
              onDownloadButtonClick={() => setShowExportAllModal(true)}
            />
          </DataTable.FooterControls>
        </DataTable>

        <LoadingOverlay show={isFetching}>
          <Spinner />
        </LoadingOverlay>

        <CreateDealModal
          origin="k12"
          isUserCompany={true}
          companyIds={dealCompanuIds}
          show={openDealModal}
          handleClose={() => setOpenDealModal(false)}
          onDealCreated={refetch}
          queryKey={TABLE_QUERY_KEY}
          filterAndSortParams={filterAndSortParams}
        />
      </TableContainer>
    )
  }
)
K12TableComponent.displayName = 'K12Table'

export const K12Table = K12TableComponent

const integerFormatterOrFallback = (
  value: number | null | undefined,
  fallback: string = '-'
) => (value ? formatInteger(value) : fallback)
const percentFormatterOrFallback = (
  value: number | null | undefined,
  fallback: string = '-'
) => {
  if (value) {
    return formatInteger(value) + '%'
  } else {
    return fallback
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useK12Columns = ({
  sorting,
}: {
  sorting?: IColumnSort<object>[]
}) => {
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const columnHelper = createColumnHelper<K12ChainProxy>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('k12district.lea_name', {
        id: 'k12district__lea_name',
        header: 'Name',
        size: 200,
        cell: (info) => {
          const name = info.getValue()
          return (
            <FbLink
              to={`/k12/${preferences?.company_type_prefix_map[ContactCompanyType.K12District]}${info.row.original.k12district.id}`}
              target="_blank"
            >
              {name}
            </FbLink>
          )
        },
      }),
      columnHelper.accessor('k12district.address', {
        id: 'k12district__address',
        meta: { tooltip: tooltips?.['opps_full_address'] },
        header: 'Address',
        size: 200,
      }),
      columnHelper.accessor('k12district.zipcode', {
        id: 'k12district__zipcode',
        meta: { tooltip: tooltips?.['opps_zipcode'] },
        header: 'Zipcode',
        size: 100,
      }),
      columnHelper.accessor('k12district.lcity', {
        id: 'k12district__lcity',
        meta: { tooltip: tooltips?.['opps_city'] },
        header: 'City',
        size: 150,
      }),
      columnHelper.accessor('k12district.lstate', {
        id: 'k12district__lstate',
        meta: { tooltip: tooltips?.['opps_full_address'] },
        header: 'State',
        size: 100,
      }),
      columnHelper.accessor('k12district.county', {
        id: 'k12district__county',
        meta: { tooltip: tooltips?.['opps_county'] },
        header: 'County',
        size: 200,
      }),
      columnHelper.accessor('k12district.locale', {
        id: 'k12district__locale',
        meta: { tooltip: tooltips?.['opps_locale'] },
        header: 'Locale',
        size: 200,
      }),
      columnHelper.accessor('k12district.operational_schools', {
        id: 'k12district__operational_schools',
        meta: { tooltip: tooltips?.['opps_schools_count'] },
        header: 'School Count',
        size: 150,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.gslo', {
        id: 'k12district__gslo',
        meta: { tooltip: tooltips?.['opps_lowest_grade'] },
        header: 'Lowest Grade',
        size: 100,
      }),
      columnHelper.accessor('k12district.gshi', {
        id: 'k12district__gshi',
        meta: { tooltip: tooltips?.['opps_highest_grade'] },
        header: 'Highest Grade',
        size: 100,
      }),
      columnHelper.accessor('k12district.student_count', {
        id: 'k12district__student_count',
        meta: { tooltip: tooltips?.['opps_students'] },
        header: 'Students',
        size: 150,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.teachers_count', {
        id: 'k12district__teachers_count',
        meta: { tooltip: tooltips?.['opps_teachers'] },
        header: 'Teachers',
        size: 150,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.phone', {
        id: 'k12district__phone',
        meta: { tooltip: tooltips?.['opps_phone'] },
        header: 'Phone',
        size: 150,
      }),
      columnHelper.accessor('deals.sales_stage', {
        id: 'sales_stages',
        meta: { tooltip: tooltips?.['opps_sales_stages'] },
        header: 'Sales Stages',
        size: 260,
        cell: (info) => {
          return (
            <SaleStagesCellFromDeals
              deals={info.row.original?.deals}
              sortAsc={
                sorting?.length && sorting[0].id === 'sales_stages'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      columnHelper.display({
        id: 'deals__account_owner',
        meta: { tooltip: tooltips?.['opps_account_owners'] },
        header: 'Account Owners',
        size: 260,
        cell: (info) => {
          const deals = info.row.original?.deals
          return <AccountOwnerCell deals={deals} />
        },
      }),

      columnHelper.accessor('taglist', {
        id: 'taglist',
        meta: { tooltip: tooltips?.['opps_tags'] },
        header: 'Tags',
        size: 260,
        cell: (info) => {
          const taglist = info.getValue()
          return <TaglistCell taglist={taglist} />
        },
      }),
      columnHelper.accessor('contact_count', {
        id: 'contact_count',
        meta: { tooltip: tooltips?.['opps_contact_count'] },
        header: 'Contacts',
        size: 110,
        cell: (info) => {
          return info.getValue() || '-'
        },
      }),
      columnHelper.accessor('note_count', {
        id: 'note_count',
        meta: { tooltip: tooltips?.['opps_note_count'] },
        header: 'Notes',
        size: 110,
        cell: (info) => {
          return info.getValue() || '-'
        },
      }),
      columnHelper.accessor('distributors', {
        id: 'distributors',
        meta: { tooltip: tooltips?.['opps_distributors'] },
        header: 'Known Distributors',
        size: 260,
        cell: (info) => {
          return (
            <DistributorsCell distributors={info.row.original?.distributors} />
          )
        },
      }),
      columnHelper.accessor('k12district.revenue', {
        id: 'k12district__revenue',
        meta: { tooltip: tooltips?.['opps_k12_revenue'] },
        header: 'Revenue',
        size: 200,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.reduced_lunch', {
        id: 'k12district__reduced_lunch',
        meta: { tooltip: tooltips?.['opps_reduced_lunch_count'] },
        header: 'Reduced Lunch',
        size: 250,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.free_lunch', {
        id: 'k12district__free_lunch',
        meta: { tooltip: tooltips?.['opps_free_lunch_count'] },
        header: 'Free Lunch',
        size: 250,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.estimated_meals_per_day', {
        id: 'k12district__estimated_meals_per_day',
        meta: { tooltip: tooltips?.['opps_k12_meals_day'] },
        header: 'Estimated Meals Per Day',
        size: 200,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.food_service_expenditures', {
        id: 'k12district__food_service_expenditures',
        meta: { tooltip: tooltips?.['opps_k12_fs_expenditures'] },
        header: 'Food Service Expenditures',
        size: 200,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.total_supplemented_lunch', {
        id: 'k12district__total_supplemented_lunch',
        meta: { tooltip: tooltips?.['opps_supplemented_lunch_count'] },
        header: 'Total Supplemented Lunch',
        size: 250,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.total_supplemented_lunch_perc', {
        id: 'k12district__total_supplemented_lunch_perc',
        meta: { tooltip: tooltips?.['opps_supplemented_lunch_percent'] },
        header: 'Total Supplemented Lunch (%)',
        size: 250,
        cell: (info) => {
          return percentFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('deal_count', {
        id: 'deal_count',
        meta: {
          tooltip: tooltips?.['opps_deals_count'],
          rightAlign: true,
        },
        header: 'Deals',
        size: 110,
        cell: (info) => {
          return info.row.original.deals?.length || '-'
        },
      }),
      columnHelper.accessor('k12district.domain', {
        id: 'k12district__domain',
        meta: { tooltip: tooltips?.['opps_domain'] },
        header: 'Domain',
        size: 200,
        cell: (info) => {
          const domain = info.getValue()
          if (!domain) {
            return '-'
          }
          return (
            <Anchor href={'//' + domain} target="_blank">
              {domain}
            </Anchor>
          )
        },
      }),
      columnHelper.accessor('k12district.google_place_url', {
        id: 'k12district__google_place_url',
        meta: { tooltip: tooltips?.['opps_google_place_url'] },
        header: 'Place URL',
        size: 200,
        cell: (info) => {
          const value = info.getValue()
          if (!value) {
            return '-'
          }
          return (
            <Anchor href={value} target="_blank">
              View
            </Anchor>
          )
        },
      }),
      columnHelper.accessor('k12district.uncategorized_lunch', {
        id: 'k12district__uncategorized_lunch',
        meta: { tooltip: tooltips?.['opps_uncat_lunch_count'] },
        header: 'Uncategorized Supplemented Lunch',
        size: 250,
        cell: (info) => {
          return integerFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.free_lunch_perc', {
        id: 'k12district__free_lunch_perc',
        meta: { tooltip: tooltips?.['opps_free_lunch_percent'] },
        header: 'Free Lunch (%)',
        size: 250,
        cell: (info) => {
          return percentFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.reduced_lunch_perc', {
        id: 'k12district__reduced_lunch_perc',
        meta: { tooltip: tooltips?.['opps_reduced_lunch_percent'] },
        header: 'Reduced Lunch (%)',
        size: 250,
        cell: (info) => {
          return percentFormatterOrFallback(info.getValue()) || '-'
        },
      }),
      columnHelper.accessor('k12district.uncategorized_lunch_perc', {
        id: 'k12district__uncategorized_lunch_perc',
        meta: { tooltip: tooltips?.['opps_uncat_lunch_percent'] },
        header: 'Uncategorized Supplemented Lunch (%)',
        size: 250,
        cell: (info) => {
          return percentFormatterOrFallback(info.getValue()) || '-'
        },
      }),
    ],
    [columnHelper]
  )

  return columns
}

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
`

const LoadingOverlay = styled.div<{
  show: boolean
}>`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(1px);
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: 30;
`
